import React from "react";
import { CreditCard, DollarSign, Smartphone, Banknote } from "lucide-react";

/**
 * Payment Methods Accepted — clean styled badges (not brand artwork).
 * Shows accepted forms of payment in a familiar "we accept" pattern.
 */
const PaymentMethods = ({ variant = "light", compact = false }) => {
  const methods = [
    {
      key: "venmo",
      label: "Venmo",
      icon: Smartphone,
      bgColor: "#0F66B7",
      textColor: "#FFFFFF",
    },
    {
      key: "visa",
      label: "Visa",
      icon: CreditCard,
      bgColor: "#1A1F71",
      textColor: "#F7B600",
    },
    {
      key: "mastercard",
      label: "Mastercard",
      icon: CreditCard,
      bgColor: "#000000",
      textColor: "#EB001B",
    },
    {
      key: "amex",
      label: "American Express",
      icon: CreditCard,
      bgColor: "#006FCF",
      textColor: "#FFFFFF",
    },
    {
      key: "debit",
      label: "Debit Cards",
      icon: CreditCard,
      bgColor: "#0F172A",
      textColor: "#FFFFFF",
    },
    {
      key: "cash",
      label: "Cash",
      icon: Banknote,
      bgColor: "#15803D",
      textColor: "#FFFFFF",
    },
  ];

  const containerBg = variant === "dark" ? "bg-slate-800/40 border-slate-700" : "bg-slate-50 border-slate-100";
  const headingColor = variant === "dark" ? "text-white" : "text-slate-900";
  const subColor = variant === "dark" ? "text-slate-300" : "text-slate-500";

  if (compact) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        {methods.map((m) => {
          const Icon = m.icon;
          return (
            <div
              key={m.key}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md font-bold text-[11px] tracking-wide"
              style={{ backgroundColor: m.bgColor, color: m.textColor }}
              aria-label={`We accept ${m.label}`}
            >
              <Icon className="h-3 w-3" />
              {m.label.toUpperCase()}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className={`rounded-2xl border p-6 ${containerBg}`}>
      <div className="flex items-center gap-2 mb-1">
        <DollarSign className="h-4 w-4 text-orange-700" aria-hidden="true" />
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-orange-700">Payment Options</span>
      </div>
      <h3
        className={`text-2xl font-extrabold mb-1 ${headingColor}`}
        style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800 }}
      >
        We Accept All Major Payment Methods
      </h3>
      <p className={`text-sm mb-5 ${subColor}`}>
        Pay how you want — credit, debit, mobile, or cash. No payment required until the repair is complete and you're satisfied.
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5">
        {methods.map((m) => {
          const Icon = m.icon;
          return (
            <div
              key={m.key}
              className="flex flex-col items-center justify-center gap-1.5 rounded-xl px-3 py-3.5 shadow-sm border border-black/10"
              style={{ backgroundColor: m.bgColor, color: m.textColor }}
              aria-label={`We accept ${m.label}`}
            >
              <Icon className="h-5 w-5" strokeWidth={2.4} />
              <span className="text-[12px] font-extrabold tracking-wide text-center leading-tight">
                {m.label.toUpperCase()}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PaymentMethods;
